import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { constants } from '../../common/constants/constants';
import {useTranslation} from 'react-i18next';
import {Col, Row} from 'react-bootstrap';
import {Form, Formik} from 'formik';
import Select from 'react-select';
import {selectCustomStyles} from '../../common/shared/select.styled';
import DatepickerNew from '../../common/DatepickerPortal';
import {StyledBankChargeForm} from './autoBankCharge.styled';

export default function BankDataForm({onContinue, address, apartmentNumber, tenantDetails = {}, className, currency}) {
    const {t} = useTranslation();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const initialFormValues = {
        bankCode: {},
        branchNumber: '',
        accountNumber: '',
        date: '',
        ownerId: ''
    };
    const validationSchema = yup.object().shape({
        branchNumber: yup.string().matches(/^\d+$/, t('titles.forms.number_field')).min(1).max(3).required(t('titles.forms.mandatory_field')),
        accountNumber: yup.string().matches(/^\d+$/, t('titles.forms.number_field')).min(6).max(9).required(t('titles.forms.mandatory_field')),
        bankCode: yup.object().required(t('titles.forms.mandatory_field')),
        date: yup.string().required(t('titles.forms.mandatory_field')),
        ownerId: yup.string().required(t('titles.forms.mandatory_field'))
    });
    const codes = currency.toUpper === 'MXN' ? constants.bankCodesInMexico : constants.bankWithPhysicalBranchesCodesInIsrael;
    const country = currency.toUpper === 'MXN' ? 'mexico' : 'israel';
    const bankOptions = codes.map(value => ({ value, label: `${value} - ${t(`common.banks.${country}.${value}`)}` }));

    return (
        <StyledBankChargeForm className={className}>
            <h3>{t('auto_charge.title')}</h3>
            <Row className="details">
                <Col>
                    <p>{t('auto_charge.address')}</p>
                    <p>{address.full_address}</p>
                </Col>
                <Col>
                    <p>{t('auto_charge.apartment')}</p>
                    <p>{apartmentNumber}</p>
                </Col>
                <Col>
                    <p>{t('auto_charge.name')}</p>
                    <p>{tenantDetails.name}</p>
                </Col>
                <Col>
                    <p>{t('auto_charge.phone')}</p>
                    <p>{tenantDetails.phone}</p>
                </Col>
            </Row>
            <Formik
                initialValues={initialFormValues}
                validationSchema={validationSchema}
            >
                {({
                      errors,
                      touched,
                      values,
                      handleChange,
                      handleBlur,
                      isValid,
                      dirty,
                      setFieldValue,
                      setFieldTouched
                  }) => (
                    <Form className="payment-details">
                        <Row>
                            <Col>
                                <div className="form-group">
                                    <label htmlFor="bank">{t('offline_payments.bank_code_title')}: </label>
                                    <Select
                                        styles={selectCustomStyles}
                                        options={bankOptions}
                                        onChange={type => setFieldValue('bankCode', type)}
                                        onBlur={() => setFieldTouched('bankCode', true)}
                                        value={values.bankCode || ''}
                                    />
                                    {
                                        errors.bankCode && touched.bankCode && (
                                            <label className={'error'}>{errors.bankCode}</label>
                                        )
                                    }
                                </div>
                            </Col>
                            <Col>
                                <div className="form-group">
                                    <label htmlFor="branchNumber">{t('offline_payments.bank_branch_title')}: </label>
                                    <input
                                        type="number"
                                        className={`bllink-input-border form-control ${errors.branchNumber && touched.branchNumber ? 'error' : ''}`}
                                        name="branchNumber"
                                        value={values.branchNumber || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    {
                                        errors.branchNumber && touched.branchNumber && (
                                            <label className={'error'}>{errors.branchNumber}</label>
                                        )
                                    }
                                </div>
                            </Col>
                            <Col>
                                <div className="form-group">
                                    <label
                                        htmlFor="accountNumber">{t('offline_payments.bank_account_number_title')}:</label>
                                    <input
                                        type="text"
                                        className={`bllink-input-border form-control ${errors.accountNumber && touched.accountNumber ? 'error' : ''}`}
                                        name="accountNumber"
                                        value={values.accountNumber || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    {
                                        errors.accountNumber && touched.accountNumber && (
                                            <label className={'error'}>{errors.accountNumber}</label>
                                        )
                                    }
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={4}>
                                <div className="form-group">
                                    <label htmlFor="ownerId">{t('auto_charge.ownerId')}:</label>
                                    <input
                                        type="number"
                                        className={`bllink-input-border form-control`}
                                        name="ownerId"
                                        value={values.ownerId || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    {
                                        errors.ownerId && touched.ownerId && (
                                            <label className={'error'}>{errors.ownerId}</label>
                                        )
                                    }
                                </div>
                            </Col>
                            <Col sm={4}>
                                <div className="form-group">
                                    <label htmlFor="date">{t('auto_charge.date')}:*</label>
                                    <div className={`${errors.date && touched.date ? 'error' : ''}`}>
                                        <DatepickerNew
                                            date={values.date || undefined}
                                            placeholder={'dd/mm/yyyy'}
                                            onChange={selectedDay => setFieldValue('date', selectedDay)}
                                            onClickOutside={() => setFieldTouched('date', true)}
                                            ltr={ltr}
                                            name='date'
                                        />
                                    </div>
                                    {
                                        errors.date && touched.date && (
                                            <label className={'error'}>{errors.date}</label>
                                        )
                                    }
                                </div>
                            </Col>
                        </Row>
                        <Row className="submit-form">
                            <Col>
                                <button
                                    type="submit"
                                    className="bllink-button"
                                    disabled={!(isValid && dirty)}
                                    onClick={(evt) => onContinue(evt, values)}
                                >
                                    {t('auto_charge.continue')}
                                </button>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Formik>
        </StyledBankChargeForm>
    )
}
